import * as React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useState, useEffect } from 'react';
import { hasValue, createQueryString } from '../../lib/utils';
import Loading from '../../commonComponents/Loading';
import { sendEvent } from '../../core/api';
import { InviteEvent } from '../../models/be/Event';
import { EventTypes } from '../../core/enums';
import { isLoggedIn } from '../../core/session';
import { getInvitationQueryStringsFromQueryOrLocalStorage } from '../../core/domain';
import { useAsyncAction } from '../../commonComponents/Hooks';
import MessageBox from '../common/MessageBox';
import { UserLoginType } from '../../models/be/SsoCustomer';
import { authenticateSSOLogin, getSsoCustomer } from './login/SsoLogin';

const Invitation = () => {
	// This component must handle 3 cases: A new user who hasn't set a password, an existing user who has logged in
	// and an existing user who hasn't logged in.
	const location = useLocation();
	const navigate = useNavigate();
	const [loading, errorMessage, performAsyncAction] = useAsyncAction(true);
	const [urlToken, workflowGroupId, companyName] = getInvitationQueryStringsFromQueryOrLocalStorage(location);
	const [userLogin, setUserLogin] = useState<UserLoginType>();

	useEffect(() => {
		performAsyncAction(async () => {
			if (hasValue(urlToken) || isLoggedIn()) {
				const event: InviteEvent = {
					type: EventTypes.AccessedInvitePage,
					setPasswordSecret: urlToken,
					workflowGroupId
				};
				const updatedIsNewUser = await sendEvent<UserLoginType>(event);
				setUserLogin(updatedIsNewUser);
			}
		});
	}, []);

	const handleSsoLogin = async (userLoginType: UserLoginType) => {
		performAsyncAction(async () => {
			if (userLoginType.isSsoUser) {
				const customerWithSsoProperty = getSsoCustomer(userLoginType.ssoCustomers, userLoginType.emailDomain);
				const redirectUri = await authenticateSSOLogin(customerWithSsoProperty, userLoginType.email, true);
				redirectUri && navigate(redirectUri);
			}
		});
	};

	// This Redirect should be before loading and errorMessage render because we should remove token from the url ASAP
	// to stop 3rd party js request to pass token to them, eg: Google Analytics.
	if (location.search.indexOf('workflowGroupId') !== -1) {
		navigate(location.pathname);
		return null;
	}

	if (errorMessage) return <MessageBox error={errorMessage} />;

	if (loading) return <Loading />;
	if (userLogin?.isSsoUser) {
		handleSsoLogin(userLogin);
	} else if (userLogin?.isNewUser === true) {
		navigate(`/set-password?secret=${urlToken}&companyName=${companyName}`);
	} else if (userLogin?.isNewUser === false || (!isLoggedIn() && !hasValue(urlToken))) {
		navigate(
			{
				pathname: '/',
				search: isLoggedIn() ? null : createQueryString({ redirectUrl: location.pathname + location.search })
			},
			{ state: { from: location } }
		);
		return null;
	}

	return <Loading />;
};

export default Invitation;
